import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ColumnDef,
    functionalUpdate,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Badge } from "@telia/teddy";
import * as properNameCase from "../../../../../shared/properNameCase";
import { API_URL_GET_ORDERS_MISSING_IMEI } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import { OrderStateEnum } from "../../types/enums";
import { OrderItem } from "../../types/models";
import { getStatusText, statusVariant } from "./orderStatusUtils";
import { getCommissionDeadlineStatus } from "../../utils";

const PAGE_SIZE = 25;

const fetchOrders = async (fromDate?: Date | null, toDate?: Date | null, agentId = ""): Promise<OrderItem[]> => {
    const url = new URL(API_URL_GET_ORDERS_MISSING_IMEI, window.location.href);
    if (fromDate) url.searchParams.set("fromDate", fromDate.toISOString());
    if (toDate) url.searchParams.set("toDate", toDate.toISOString());
    if (agentId) url.searchParams.set("agentId", agentId);
    const response = await retailerFetch(url.toString());
    if (!response.ok) throw new Error("Kunne ikke hente ordre. Prøv igjen senere.");
    const result = await response.json();
    return result?.registrations ?? [];
};

const EMPTY_ORDERS: OrderItem[] = [];

interface UseOrdersMissingImeiTableOptions {
    agentId: string;
    enabled: boolean;
    endDate: Date | null;
    onPageIndexChange: (pageIndex: number) => void;
    onRowClickCb?: () => void;
    pageIndex: number;
    search: string;
    startDate: Date | null;
}

const useOrdersMissingImeiTable = ({
    agentId,
    enabled,
    endDate,
    onPageIndexChange,
    onRowClickCb,
    pageIndex,
    search,
    startDate,
}: UseOrdersMissingImeiTableOptions) => {
    const queryClient = useQueryClient();
    const pagination = { pageIndex, pageSize: PAGE_SIZE };
    const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);

    const queryKey = ["ordersMissingImei", startDate?.toISOString(), endDate?.toISOString(), agentId];

    const {
        data: fetchedOrders,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: () => fetchOrders(startDate, endDate, agentId),
        enabled,
        retry: false,
    });
    const orders = fetchedOrders ?? EMPTY_ORDERS;

    const removeOrder = useCallback(
        (orderId: number) => {
            queryClient.setQueryData<OrderItem[]>(queryKey, (old) =>
                old ? old.filter((o) => o.orderId !== orderId) : [],
            );
            refetch();
        },
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
        [queryClient, refetch, startDate, endDate],
    );

    const filteredData = useMemo(() => {
        if (!search) return orders;
        const searchLower = search.toLowerCase();
        return orders.filter(
            (order) =>
                String(order.orderId).includes(searchLower) ||
                (order.legalResponsibleName?.toLowerCase().includes(searchLower) ?? false) ||
                (order.orderState ? getStatusText(order.orderState).toLowerCase().includes(searchLower) : false),
        );
    }, [orders, search]);

    useEffect(() => {
        if (!enabled || !fetchedOrders) return;
        const lastPageIndex = Math.max(0, Math.ceil(filteredData.length / PAGE_SIZE) - 1);
        if (pageIndex > lastPageIndex) onPageIndexChange(lastPageIndex);
    }, [enabled, fetchedOrders, filteredData.length, onPageIndexChange, pageIndex]);

    const toggleExpansion = (orderId: number) => {
        setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
    };

    const columns = useMemo<ColumnDef<OrderItem>[]>(
        () => [
            {
                accessorKey: "orderId",
                id: "ref",
                header: "Ordre-ID",
            },
            {
                accessorKey: "legalResponsibleName",
                id: "legalResponsibleName",
                header: "Kunde",
                cell: ({ getValue }) => <span data-di-mask>{properNameCase.convert(getValue<string>() ?? "")}</span>,
            },
            {
                accessorKey: "orderState",
                id: "orderState",
                header: "Status",
                cell: ({ getValue }) => {
                    const val = getValue<OrderStateEnum | undefined>();
                    if (!val) return "";
                    return (
                        <Badge variant={statusVariant(val)} hideIcon>
                            {getStatusText(val)}
                        </Badge>
                    );
                },
            },
            {
                accessorKey: "salesAgent",
                id: "salesAgent",
                header: "Selger ID",
                cell: ({ getValue }) => {
                    const val = getValue<string | undefined>();
                    return <span data-di-mask>{!val || val === "NOT_DEFINED" ? "Ikke angitt" : val}</span>;
                },
            },
            {
                accessorKey: "creationDate",
                header: "Opprettet",
                cell: ({ getValue }) => {
                    const val = getValue<string | undefined>();
                    if (!val) return "";
                    const d = new Date(val);
                    return isNaN(d.getTime())
                        ? val
                        : d.toLocaleString("nb-NO", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                          });
                },
            },
            {
                accessorKey: "creationDate",
                id: "commissionDeadline",
                header: "Frist",
                cell: ({ getValue }) => {
                    const val = getValue<string | undefined>();
                    if (!val) return "";
                    const status = getCommissionDeadlineStatus(val);
                    if (!status) return "";
                    return (
                        <Badge variant={status.variant} hideIcon>
                            {status.label}
                        </Badge>
                    );
                },
            },
        ],
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
        [],
    );

    const table = useReactTable({
        columns,
        data: filteredData,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        autoResetPageIndex: false,
        state: { pagination },
        onPaginationChange: (updater) => onPageIndexChange(functionalUpdate(updater, pagination).pageIndex),
    });

    const onRowClick = (row: OrderItem) => {
        if (!row.orderId) {
            return;
        }
        toggleExpansion(row.orderId);
        if (onRowClickCb && expandedOrderId) {
            onRowClickCb();
        }
    };

    return {
        columns,
        error,
        expandedOrderId,
        isLoading,
        onRowClick,
        refetch,
        removeOrder,
        setExpandedOrderId,
        table,
    };
};

export default useOrdersMissingImeiTable;
